<template>
  <div class="cm-root" :data-open="String(isOpen)">
    <div class="cm-overlay" part="overlay" @click="close"></div>
    <div class="cm-dialog-wrapper" :class="{ 'cm-shake': isShaking }">
      <div class="cm-dialog" part="dialog" role="dialog" aria-modal="true">
        <div class="cm-header">
          <div class="cm-brand">
            <div v-if="state.optsRef.value.brandName" class="cm-brand-title">{{ brandTitle }}</div>
            <div>
              <div class="cm-title">{{ headerTitle }}</div>
              <div class="cm-subtitle">{{ headerSubtitle }}</div>
            </div>
          </div>
          <div class="cm-header-actions">
            <LanguageSelector />
            <button class="cm-close" type="button" aria-label="Close" @click="close">✕</button>
          </div>
        </div>

        <div class="cm-body">
          <div class="cm-step" :data-active="String(step === 1)">
            <StepOne
              ref="stepOneRef"
              :model="stepOneModel"
              :errors="{ email: errors.email, acceptPrivacyPolicy: errors.acceptPrivacyPolicy }"
              @update:model="updateStepOne"
            />
          </div>

          <div class="cm-step" :data-active="String(step === 2)">
            <StepTwo
              ref="stepTwoRef"
              :model="stepTwoModel"
              :errors="{
                firstName: errors.firstName,
                lastName: errors.lastName,
                country: errors.country,
                phone: errors.phone,
                message: errors.message,
              }"
              :preferred-countries="preferredCountries"
              @update:model="updateStepTwo"
            />
          </div>

          <div class="cm-step" :data-active="String(step === 3)">
            <SuccessStep />
          </div>
        </div>

        <div class="cm-footer" part="footer">
          <button
            v-if="step === 1"
            class="cm-btn cm-btn-secondary"
            type="button"
            :disabled="isBusy"
            @click="close"
          >
            {{ t('contactForm.cancel') }}
          </button>

          <button
            v-if="step === 2"
            class="cm-btn cm-btn-secondary"
            type="button"
            :disabled="isBusy"
            @click="goBack"
          >
            {{ t('contactForm.back') }}
          </button>

          <button class="cm-btn cm-btn-primary" type="button" :disabled="isBusy" @click="primary">
            <template v-if="step === 1">{{
              isBusy ? t('contactForm.working') : t('contactForm.continue')
            }}</template>
            <template v-else-if="step === 2">{{
              isBusy ? t('contactForm.sending') : t('contactForm.send')
            }}</template>
            <template v-else>{{ t('contactForm.close') }}</template>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import StepOne from './StepOne.vue'
  import StepTwo from './StepTwo.vue'
  import SuccessStep from './SuccessStep.vue'
  import LanguageSelector from './LanguageSelector.vue'
  import type {
    ContactMeOpenDetail,
    ContactMeSubmitPayload,
    ContactMeWidgetOptions,
  } from '../types'
  import type { SupportedLocale } from '../types'
  import type { StepOneModel, StepTwoModel } from '../types'
  import { normalizeWhitespace } from '../utils/validation'
  import { detectLocale } from '../i18n/detect'
  import { getDefaultCountryForLocale } from '../utils/localeToCountry'
  import {
    useContactFormState,
    useContactFormValidation,
    useContactFormSubmit,
    useContactMeEvents,
    useContactMeAnalytics,
  } from '../composables'

  const preferredCountries = ['SE', 'NO', 'FI', 'DK', 'ES', 'DE', 'FR', 'GB', 'US']

  const { t, locale } = useI18n({ useScope: 'global' })
  const state = useContactFormState({ t })

  // Initialize country based on detected locale
  state.updateForm({ country: getDefaultCountryForLocale(locale.value) })

  // Unwrapped for template (refs are Ref<T>, template needs plain values for typecheck)
  const isOpen = computed(() => state.isOpen.value)
  const step = computed(() => state.step.value)
  const isBusy = computed(() => state.isBusy.value)
  const isShaking = computed(() => state.isShaking.value)
  const errors = computed(() => state.errors.value)
  const brandTitle = computed(() => state.brandTitle.value)
  const headerTitle = computed(() => state.headerTitle.value)
  const headerSubtitle = computed(() => state.headerSubtitle.value)
  const stepOneModel = computed(() => state.stepOneModel.value)
  const stepTwoModel = computed(() => state.stepTwoModel.value)
  const events = useContactMeEvents()
  const analytics = useContactMeAnalytics()
  const validation = useContactFormValidation(
    state.form,
    state.errors,
    t,
    state.setError,
    state.shake
  )
  const { submitInitial, submitFinal } = useContactFormSubmit(
    state.form,
    state.optsRef,
    state.openDetail,
    state.isBusy,
    state.setError,
    t,
    payload => events.emitInitialSubmitted(payload),
    err => {
      events.emitError('initial', err)
      state.shake()
    },
    payload => events.emitSubmitted(payload),
    err => {
      const propertyId = getPropertyIdFromContext(state.openDetail.value?.context)
      events.emitError('final', err)
      analytics.trackSubmitError({
        propertyId,
        code: err,
      })
      state.shake()
    }
  )

  const stepOneRef = ref<InstanceType<typeof StepOne> | null>(null)
  const stepTwoRef = ref<InstanceType<typeof StepTwo> | null>(null)

  function getPropertyIdFromContext(context?: ContactMeOpenDetail['context']): string | undefined {
    if (!context || typeof context !== 'object') return undefined
    const id =
      (context as Record<string, unknown>).propertyId ??
      (context as Record<string, unknown>).property_id
    return id != null ? String(id) : undefined
  }

  function updateStepOne(m: StepOneModel) {
    state.updateForm({
      email: m.email,
      acceptPrivacyPolicy: m.acceptPrivacyPolicy,
      acceptMarketing: m.acceptMarketing,
    })
  }

  function updateStepTwo(m: StepTwoModel) {
    state.updateForm({
      firstName: m.firstName,
      lastName: m.lastName,
      country: m.country,
      phone: m.phone,
      hasWhatsApp: m.hasWhatsApp,
      message: m.message,
    })
  }

  let autoCloseTimer: number | undefined
  function showSuccessAndAutoClose() {
    state.step.value = 3
    // Add vanish class after 3 seconds for elegant fade out
    if (autoCloseTimer) window.clearTimeout(autoCloseTimer)
    autoCloseTimer = window.setTimeout(() => {
      if (state.step.value === 3) {
        // Add vanish class for animation
        const wrapper = document.querySelector('.cm-dialog-wrapper') as HTMLElement
        if (wrapper) {
          wrapper.classList.add('cm-vanish')
          // Close after animation completes
          setTimeout(() => close(), 800)
        } else {
          close()
        }
      }
    }, 3000)
  }

  function buildPayload(): ContactMeSubmitPayload {
    return {
      firstName: normalizeWhitespace(state.form.value.firstName),
      lastName: normalizeWhitespace(state.form.value.lastName),
      email: state.form.value.email.trim(),
      country: state.form.value.country.trim().toUpperCase(),
      phone: state.form.value.phone.trim(),
      hasWhatsApp: state.form.value.hasWhatsApp,
      message: state.form.value.message.trim(),
      acceptPrivacyPolicy: state.form.value.acceptPrivacyPolicy,
      acceptMarketing: state.form.value.acceptMarketing,
      source: state.optsRef.value.source ?? 'website',
      utm_source: typeof window !== 'undefined' ? window.location.href : '',
      context: state.openDetail.value?.context,
    }
  }

  async function primary() {
    if (state.step.value === 1) {
      if (!validation.validateStep1()) return
      getPropertyIdFromContext(state.openDetail.value?.context)
      analytics.trackStep1Submit(state.openDetail.value)
      await submitInitial()
      state.step.value = 2
      await nextTick()
      ;(stepTwoRef.value?.$el as HTMLElement | undefined)
        ?.querySelector<HTMLInputElement>('#cm-firstName')
        ?.focus()
      return
    }
    if (state.step.value === 2) {
      if (!validation.validateStep2()) return
      const payload = buildPayload()
      const propertyId = getPropertyIdFromContext(state.openDetail.value?.context)
      events.emitSubmitAttempt(payload)
      analytics.trackSubmitAttempt(propertyId)
      await submitFinal(buildPayload, conversationId => {
        analytics.trackStep2Submit(propertyId)
        analytics.trackConversion({ conversationId, propertyId })
        analytics.trackSubmitSuccess(propertyId)
        showSuccessAndAutoClose()
      })
      return
    }
    close()
  }

  function goBack() {
    state.step.value = 1
    nextTick(() =>
      (stepOneRef.value?.$el as HTMLElement | undefined)
        ?.querySelector<HTMLInputElement>('#cm-email')
        ?.focus()
    )
  }

  function applyLocale() {
    const next: SupportedLocale =
      !state.optsRef.value.lang || state.optsRef.value.lang === 'auto'
        ? detectLocale()
        : (state.optsRef.value.lang as SupportedLocale)
    locale.value = next
  }

  async function open(detail?: ContactMeOpenDetail) {
    applyLocale()
    analytics.init(state.optsRef.value.gtagId)
    state.openDetail.value = detail
    state.applyPrefill(detail?.prefill)
    state.step.value = 1
    state.isOpen.value = true
    analytics.trackOpen(detail)
    events.emitOpened(detail)
    await nextTick()
    ;(stepOneRef.value?.$el as HTMLElement | undefined)
      ?.querySelector<HTMLInputElement>('#cm-email')
      ?.focus()
  }

  function close() {
    if (!state.isOpen.value) return
    state.isOpen.value = false
    state.reset()
    events.emitClosed()
  }

  function configure(o: ContactMeWidgetOptions) {
    state.optsRef.value = { ...state.optsRef.value, ...o }
    applyLocale()
  }

  // Listen for locale changes from language selector
  const onLocaleChange = (e: Event) => {
    const detail = (e as CustomEvent<{ locale: SupportedLocale | 'auto' }>).detail
    if (detail?.locale) {
      if (detail.locale === 'auto') {
        applyLocale() // Re-run auto-detection
      } else {
        locale.value = detail.locale
        // Prefill country based on selected locale
        state.updateForm({ country: getDefaultCountryForLocale(detail.locale) })
      }
    }
  }

  onMounted(() => {
    window.addEventListener('contactme:change-locale', onLocaleChange)
  })

  onUnmounted(() => {
    window.removeEventListener('contactme:change-locale', onLocaleChange)
  })

  defineExpose({ open, close, configure })

  onBeforeUnmount(() => {
    if (autoCloseTimer) window.clearTimeout(autoCloseTimer)
  })
</script>
